import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import PanelLayout from '../layouts/PanelLayout';
import { authorService } from '../../services/authorService';
import ActionMenu from '../../components/common/ActionMenu';
import Loader from '../../components/Loader';
import Button from '../../components/common/Button';
import ConfirmModal from '../../components/common/ConfirmModal';
import SearchableSelect from '../../components/common/SearchableSelect';
import Pagination from '../../components/common/Pagination';

const STATUS_FILTER_OPTIONS = [
   { id: '', name: 'All Statuses' },
   { id: 'ACTIVE', name: 'Active' },
   { id: 'INACTIVE', name: 'Inactive' },
];

const LIMIT = 20;

const Authors = () => {
   const navigate = useNavigate();
   const [authors, setAuthors] = useState([]);
   const [loading, setLoading] = useState(true);
   const [page, setPage] = useState(1);
   const [totalPages, setTotalPages] = useState(1);
   const [total, setTotal] = useState(0);
   const [searchInput, setSearchInput] = useState('');
   const [search, setSearch] = useState('');
   const [statusFilter, setStatusFilter] = useState('');
   const [deleteTarget, setDeleteTarget] = useState(null);
   const [deleting, setDeleting] = useState(false);
   const debounceRef = useRef(null);

   useEffect(() => {
      fetchAuthors();
   }, [page, search, statusFilter]);

   const fetchAuthors = async () => {
      try {
         setLoading(true);
         const params = { page, limit: LIMIT };
         if (statusFilter) params.status = statusFilter;
         if (search.trim()) params.search = search.trim();
         const response = await authorService.getAll(params);
         setAuthors(response.data);
         setTotalPages(response.pagination.totalPages);
         setTotal(response.pagination.total);
      } catch (error) {
         console.error('Error fetching authors:', error);
         toast.error('Failed to fetch authors');
      } finally {
         setLoading(false);
      }
   };

   const handleSearchChange = (e) => {
      const value = e.target.value;
      setSearchInput(value);
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
         setSearch(value);
         setPage(1);
      }, 400);
   };

   const handleDelete = async () => {
      if (!deleteTarget) return;
      try {
         setDeleting(true);
         await authorService.delete(deleteTarget.id);
         toast.success('Author deleted successfully');
         setDeleteTarget(null);
         setPage(1);
         fetchAuthors();
      } catch (error) {
         console.error('Error deleting author:', error);
         toast.error(error.response?.data?.message || 'Failed to delete author');
      } finally {
         setDeleting(false);
      }
   };

   if (loading) {
      return (
         <PanelLayout>
            <Loader />
         </PanelLayout>
      );
   }

   return (
      <PanelLayout>
         <div className="space-y-6">
            <div className="flex items-center justify-between">
               <div>
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Authors</h1>
                  <p className="mt-2 text-gray-600 dark:text-gray-400">
                     {total} author{total !== 1 ? 's' : ''}
                  </p>
               </div>
               <Button onClick={() => navigate('/admin/authors/new')}>Create New Author</Button>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
               <div className="relative">
                  <span className="absolute inset-y-0 left-3 flex items-center text-gray-400 dark:text-gray-500">
                     <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth={2}
                           d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
                        />
                     </svg>
                  </span>
                  <input
                     type="text"
                     value={searchInput}
                     onChange={handleSearchChange}
                     placeholder="Search by name or email..."
                     className="pl-9 pr-3 min-h-[42px] border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-primary text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-dark-accent focus:border-transparent text-sm w-64"
                  />
               </div>
               <div className="w-40">
                  <SearchableSelect
                     searchable={false}
                     placeholder="All Statuses"
                     options={STATUS_FILTER_OPTIONS}
                     value={statusFilter}
                     onChange={(value) => {
                        setStatusFilter(value);
                        setPage(1);
                     }}
                     className="!mb-0"
                  />
               </div>
               {(searchInput || statusFilter) && (
                  <button
                     onClick={() => {
                        setSearchInput('');
                        setSearch('');
                        setStatusFilter('');
                        setPage(1);
                     }}
                     className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                  >
                     Clear filters
                  </button>
               )}
            </div>

            <div className="rounded-lg bg-white dark:bg-dark-secondary shadow-md overflow-hidden">
               <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                     <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                           Author
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                           Email
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                           Blogs
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                           Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                           Actions
                        </th>
                     </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-dark-secondary divide-y divide-gray-200 dark:divide-gray-700">
                     {authors.length === 0 ? (
                        <tr>
                           <td
                              colSpan="5"
                              className="px-6 py-10 text-center text-gray-500 dark:text-gray-400"
                           >
                              No authors found
                           </td>
                        </tr>
                     ) : (
                        authors.map((author) => (
                           <tr
                              key={author.id}
                              onClick={() => navigate(`/admin/authors/${author.id}`)}
                              className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                           >
                              <td className="px-6 py-4 whitespace-nowrap">
                                 <div className="flex items-center gap-3">
                                    {author.image ? (
                                       <img
                                          src={author.image}
                                          alt={author.name}
                                          className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                                       />
                                    ) : (
                                       <div className="w-9 h-9 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                                          <span className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                                             {author.name.charAt(0).toUpperCase()}
                                          </span>
                                       </div>
                                    )}
                                    <div>
                                       <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                          {author.name}
                                       </div>
                                       <div className="text-xs text-gray-500 dark:text-gray-400">
                                          /{author.slug}
                                       </div>
                                    </div>
                                 </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                                 {author.email || 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                                 {author._count?.blogs ?? 0}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                 <span
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                       author.status === 'ACTIVE'
                                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                          : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                                    }`}
                                 >
                                    {author.status}
                                 </span>
                              </td>
                              <td
                                 className="px-6 py-4 whitespace-nowrap"
                                 onClick={(e) => e.stopPropagation()}
                              >
                                 <ActionMenu
                                    items={[
                                       {
                                          label: 'Edit',
                                          onClick: () => navigate(`/admin/authors/${author.id}`),
                                       },
                                       {
                                          label: 'Delete',
                                          variant: 'danger',
                                          onClick: () => setDeleteTarget(author),
                                       },
                                    ]}
                                 />
                              </td>
                           </tr>
                        ))
                     )}
                  </tbody>
               </table>
            </div>

            <Pagination
               page={page}
               totalPages={totalPages}
               total={total}
               limit={LIMIT}
               onPageChange={setPage}
            />
         </div>

         <ConfirmModal
            isOpen={!!deleteTarget}
            title="Delete Author"
            message={`Are you sure you want to delete "${deleteTarget?.name}"? This cannot be undone. Authors with assigned blogs cannot be deleted.`}
            onConfirm={handleDelete}
            onCancel={() => setDeleteTarget(null)}
            loading={deleting}
         />
      </PanelLayout>
   );
};

export default Authors;
